// 단축URL 생성 폼 — 고급 옵션(만료·클릭제한·비밀번호)은 접어둔다
import { useState, type FormEvent } from "react";
import { linkApi, type Link } from "../api/links.ts";
import { ApiError } from "@taplink/web-shared";

interface Props {
  onCreated: (link: Link) => void;
  /** 만료·클릭제한·비밀번호는 유료 기능(ACCESS_CONTROL) — 사용 불가면 잠금 표시 */
  accessControlEnabled: boolean;
}

export function LinkCreateForm({ onCreated, accessControlEnabled }: Props) {
  const [originalUrl, setOriginalUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expireAt, setExpireAt] = useState("");
  const [maxClicks, setMaxClicks] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setOriginalUrl("");
    setExpireAt("");
    setMaxClicks("");
    setPassword("");
    setShowAdvanced(false);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await linkApi.create({
        originalUrl: originalUrl.trim(),
        // 요금제가 허용하지 않으면 남아있는 입력값을 보내지 않는다(서버 403 방지)
        ...(accessControlEnabled
          ? {
              // datetime-local 값에는 타임존이 없다 → ISO 오프셋을 붙여 서버 해석을 명확히 한다
              expireAt: expireAt ? new Date(expireAt).toISOString() : null,
              maxClicks: maxClicks ? Number(maxClicks) : null,
              password: password || null,
            }
          : {}),
      });
      onCreated(created);
      reset();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "링크 생성에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="panel">
      <h2>새 단축 링크</h2>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="field">
        <label htmlFor="originalUrl">원본 URL</label>
        <input
          id="originalUrl"
          type="url"
          required
          placeholder="https://example.com/very/long/path"
          value={originalUrl}
          onChange={(e) => setOriginalUrl(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="btn-link"
        aria-expanded={showAdvanced}
        disabled={!accessControlEnabled}
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {!accessControlEnabled
          ? "고급 옵션 (Lite 이상 요금제)"
          : showAdvanced
            ? "고급 옵션 접기"
            : "고급 옵션 (만료·클릭제한·비밀번호)"}
      </button>

      {showAdvanced && accessControlEnabled && (
        <div className="advanced">
          <div className="field">
            <label htmlFor="expireAt">만료 일시</label>
            <input
              id="expireAt"
              type="datetime-local"
              value={expireAt}
              onChange={(e) => setExpireAt(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="maxClicks">최대 클릭 수</label>
            <input
              id="maxClicks"
              type="number"
              min={1}
              placeholder="제한 없음"
              value={maxClicks}
              onChange={(e) => setMaxClicks(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="linkPassword">비밀번호</label>
            <input
              id="linkPassword"
              type="password"
              autoComplete="off"
              placeholder="설정 안 함"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="link-password-hint"
            />
            <p className="hint" id="link-password-hint">
              비밀번호를 설정하면 추측이 어려운 임의 키가 발급됩니다.
            </p>
          </div>
        </div>
      )}

      <button type="submit" className="btn-primary btn-block" disabled={submitting || !originalUrl}>
        {submitting ? "생성 중…" : "단축 링크 만들기"}
      </button>
    </form>
  );
}
